/* Horizon: Hide non-existent variant combinations (sticky + attribute-safe)
 *
 * ✅ hides NON-EXISTENT combos ("non variants")
 * ✅ optional sold-out filtering
 * ✅ starts AFTER first user interaction
 * ✅ NEVER filters option group 0 (top)
 * ✅ auto-selects first valid value in option group 1 (second row)
 * ✅ survives Horizon updates that only change ATTRIBUTES (the cause of your “unhide”)
 */

(() => {
  const CONFIG = {
    hideNonExistentCombos: true,
    hideSoldOutCombos: false,

    buttonsAction: "hide",
    dropdownAction: "hide",

    // Never hide/auto-change option group 0 (Model / primary choice).
    skipFilterIndexes: [0],
    // When set, ensureValidSelections never rewrites group 0 (belt-and-braces for fitment products).
    anchorFirstOptionAttr: "data-ctc-anchor-first-option",
    anchorOptionCountAttr: "data-ctc-anchor-option-count",
    skipFilterIndexesAttr: "data-ctc-skip-filter-indexes",
    ensureAllGroups: true
  };

  const productCache = new Map();
  const wired = new WeakSet();

  // per-picker state
  const hasInteracted = new WeakMap();
  const autoSelecting = new WeakMap();

  // scheduling
  const rafPending = new WeakMap();
  const rafAgain = new WeakMap();

  const qsa = (root, sel) => Array.from(root.querySelectorAll(sel));

  function cleanProductUrl(url) {
    if (!url) return url;
    return url.split("?")[0].split("#")[0];
  }

  function normalizeProduct(product) {
    const variants = (product.variants || []).map(v => ({
      id: v.id,
      available: !!v.available,
      options: Array.isArray(v.options)
        ? v.options
        : [v.option1, v.option2, v.option3].filter(Boolean)
    }));
    return { variants };
  }

  async function fetchProductJson(productUrlRaw) {
    const productUrl = cleanProductUrl(productUrlRaw);
    const url = `${productUrl}.js`;

    if (productCache.has(url)) return productCache.get(url);

    const p = fetch(url, { credentials: "same-origin" })
      .then(r => {
        if (!r.ok) throw new Error(`Failed to fetch ${url} (${r.status})`);
        return r.json();
      })
      .then(normalizeProduct);

    productCache.set(url, p);
    return p;
  }

  const shouldSkipIndex = (vp, i) => {
    const attr = vp?.getAttribute?.(CONFIG.skipFilterIndexesAttr);
    const indexes = attr
      ? attr.split(",").map((s) => parseInt(s.trim(), 10)).filter((n) => !Number.isNaN(n))
      : CONFIG.skipFilterIndexes;
    return Array.isArray(indexes) && indexes.includes(i);
  };

  function ensureValidStartIndex(vp) {
    if (vp?.hasAttribute?.(CONFIG.anchorFirstOptionAttr)) {
      const n = parseInt(vp.getAttribute(CONFIG.anchorOptionCountAttr) || "1", 10);
      return Math.max(1, Number.isNaN(n) ? 1 : n);
    }
    return 1;
  }

  const isInternalOptionInput = (input) =>
    input?.classList?.contains("ctc-logo-colour-na");

  const isInternalOption = (opt) =>
    opt?.classList?.contains("ctc-logo-colour-na");

  function isOptionValueVisible(input) {
    if (!input || isInternalOptionInput(input)) return false;
    const label = input.closest("label");
    if (label?.classList?.contains("ctc-hidden")) return false;
    if (label?.hidden || input.hidden) return false;
    return true;
  }

  function updateGroupVisibility(groups) {
    for (const group of groups) {
      if (group.type === "buttons") {
        const fs = group.inputs[0]?.closest("fieldset");
        if (!fs || fs.dataset.ctcManagedHidden) continue;

        const hasVisible = group.inputs.some((input) => isOptionValueVisible(input));
        fs.hidden = !hasVisible;
        fs.setAttribute("aria-hidden", hasVisible ? "false" : "true");
      } else if (group.type === "dropdown") {
        const wrapper = group.element.closest(".variant-option--dropdowns");
        if (!wrapper || wrapper.dataset.ctcManagedHidden) continue;

        const hasVisible = Array.from(group.element.options).some(
          (opt) => opt.value && !isInternalOption(opt) && !opt.hidden
        );
        wrapper.hidden = !hasVisible;
        wrapper.setAttribute("aria-hidden", hasVisible ? "false" : "true");
      }
    }
  }

  function getGroups(vp) {
    // Buttons/radios
    const fieldsets = qsa(vp, 'fieldset.variant-option[data-fieldset-index]');
    const buttonGroups = fieldsets.map(fs => {
      const index = parseInt(fs.getAttribute("data-fieldset-index") || "0", 10);
      const inputs = qsa(fs, 'input[type="radio"][data-fieldset-index]').filter(
        (i) => !isInternalOptionInput(i)
      );

      return {
        type: "buttons",
        index,
        inputs,
        values() { return inputs.map(i => i.value); },
        getValue() {
          const checked = inputs.find(i => i.checked);
          return checked ? checked.value : "";
        },
        isValueAllowed(value) {
          const input = inputs.find(i => i.value === value);
          if (!input) return false;
          const label = input.closest("label") || input.parentElement;
          if (input.disabled) return false;
          if (label?.classList?.contains("ctc-hidden")) return false;
          return true;
        },
        selectValue(value) {
          const input = inputs.find(i => i.value === value);
          if (!input) return false;
          const label = input.closest("label") || input.parentElement;
          if (input.disabled) return false;
          if (label?.classList?.contains("ctc-hidden")) return false;

          input.checked = true;
          input.dispatchEvent(new Event("change", { bubbles: true }));
          return true;
        },
        setAvailability(value, allowed) {
          const input = inputs.find(i => i.value === value);
          if (!input) return;

          const label = input.closest("label") || input.parentElement;

          if (CONFIG.buttonsAction === "disable") {
            input.disabled = !allowed;
            label.classList.toggle("ctc-disabled", !allowed);
            label.classList.remove("ctc-hidden");
            if (!allowed && input.checked) input.checked = false;
          } else {
            input.disabled = !allowed;
            label.classList.toggle("ctc-hidden", !allowed); // ✅ sticky hide via CSS !important
            label.classList.remove("ctc-disabled");
            if (!allowed && input.checked) input.checked = false;
          }
        }
      };
    });

    // Dropdowns/selects
    const selects = qsa(vp, 'select.variant-option__select[name^="options["]');
    const dropdownGroups = selects.map((sel, domIndex) => {
      const index = domIndex;
      return {
        type: "dropdown",
        index,
        element: sel,
        values() {
          return Array.from(sel.options)
            .filter((o) => o.value != null && o.value !== "" && !isInternalOption(o))
            .map((o) => o.value);
        },
        getValue() { return sel.value || ""; },
        isValueAllowed(value) {
          const opt = Array.from(sel.options).find(o => o.value === value);
          if (!opt) return false;
          return !(opt.disabled || opt.hidden);
        },
        selectValue(value) {
          const opt = Array.from(sel.options).find(o => o.value === value);
          if (!opt || opt.disabled || opt.hidden) return false;
          sel.value = value;
          sel.dispatchEvent(new Event("change", { bubbles: true }));
          return true;
        },
        setAvailability(value, allowed) {
          const opt = Array.from(sel.options).find(o => o.value === value);
          if (!opt) return;

          if (CONFIG.dropdownAction === "hide") {
            opt.hidden = !allowed;
            opt.disabled = false;
          } else {
            opt.disabled = !allowed;
            opt.hidden = false;
          }

          if (!allowed && sel.value === value) sel.value = "";
        }
      };
    });

    return [...buttonGroups, ...dropdownGroups].sort((a, b) => a.index - b.index);
  }

  function buildAllowedMaps(variants, groupCount, selections, includeSoldOut) {
    const allowed = Array.from({ length: groupCount }, () => new Set());

    for (const v of variants) {
      if (!includeSoldOut && !v.available) continue;

      for (let i = 0; i < groupCount; i++) {
        let ok = true;

        for (let j = 0; j < groupCount; j++) {
          if (j === i) continue;
          const sel = selections[j];
          if (!sel) continue;
          if (v.options[j] !== sel) { ok = false; break; }
        }

        if (ok && v.options[i] != null) allowed[i].add(v.options[i]);
      }
    }

    return allowed;
  }

  function ensureValidSelections(vp, groups, allowedSets) {
    if (autoSelecting.get(vp)) return;
    autoSelecting.set(vp, true);

    try {
      // Only auto-fix options at/after anchor count (e.g. tank skirts: fix profile only).
      const startIndex = ensureValidStartIndex(vp);

      for (let i = startIndex; i < groups.length; i++) {
        const current = groups[i].getValue();
        const allowed = allowedSets[i];

        if (current && allowed.has(current) && groups[i].isValueAllowed(current)) continue;

        const firstAllowed = groups[i].values().find(v => allowed.has(v));
        if (firstAllowed) groups[i].selectValue(firstAllowed);
      }
    } finally {
      setTimeout(() => autoSelecting.set(vp, false), 0);
    }
  }

  function scheduleRecalc(vp, variants) {
    if (rafPending.get(vp)) {
      rafAgain.set(vp, true);
      return;
    }

    rafPending.set(vp, true);
    rafAgain.set(vp, false);

    requestAnimationFrame(() => {
      rafPending.set(vp, false);

      if (!hasInteracted.get(vp)) return;

      // Ignore loops: during our own update we set a flag
      if (vp.__ctcUpdating) return;

      vp.__ctcUpdating = true;

      try {
        const groups = getGroups(vp);
        if (!groups.length) return;

        const optionCount = Math.max(...variants.map(v => v.options.length));
        const groupCount = Math.min(groups.length, optionCount);

        const selections = [];
        for (let i = 0; i < groupCount; i++) selections[i] = groups[i].getValue() || "";

        if (CONFIG.hideNonExistentCombos) {
          const allowedAny = buildAllowedMaps(variants, groupCount, selections, true);

          for (let i = 0; i < groupCount; i++) {
            if (shouldSkipIndex(vp, i)) continue;
            for (const val of groups[i].values()) {
              groups[i].setAvailability(val, allowedAny[i].has(val));
            }
          }

          ensureValidSelections(vp, groups, allowedAny);
        }

        if (CONFIG.hideSoldOutCombos) {
          const allowedAvail = buildAllowedMaps(variants, groupCount, selections, false);

          for (let i = 0; i < groupCount; i++) {
            if (shouldSkipIndex(vp, i)) continue;
            for (const val of groups[i].values()) {
              groups[i].setAvailability(val, allowedAvail[i].has(val));
            }
          }

          ensureValidSelections(vp, groups, allowedAvail);
        }

        updateGroupVisibility(groups);
      } finally {
        // Release flag after this tick
        setTimeout(() => { vp.__ctcUpdating = false; }, 0);
      }

      if (rafAgain.get(vp)) {
        rafAgain.set(vp, false);
        scheduleRecalc(vp, variants);
      }
    });
  }

  function attachPickerObserver(vp, variants) {
    if (vp.__ctcObserverAttached) return;
    vp.__ctcObserverAttached = true;

    const obs = new MutationObserver((mutations) => {
      // Skip mutations caused by our own class toggles
      if (vp.__ctcUpdating) return;

      // If Horizon updates availability by attributes only, we still want to run.
      // This catches “unhide” cases.
      scheduleRecalc(vp, variants);
    });

    obs.observe(vp, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ["class", "style", "disabled", "aria-disabled", "data-option-available", "data-current-checked"]
    });
  }

  async function wireVariantPicker(vp) {
    if (wired.has(vp)) return;
    // Tank skirt products use ctc-tank-skirt-variant-picker.js (profile must not rewrite model/shape).
    if (vp.hasAttribute(CONFIG.anchorOptionCountAttr)) return;
    wired.add(vp);

    const productUrlRaw = vp.getAttribute("data-product-url");
    if (!productUrlRaw) return;

    const { variants } = await fetchProductJson(productUrlRaw);
    if (!variants?.length) return;

    hasInteracted.set(vp, true);

    // Hide invalid combos immediately on load, then on every change
    scheduleRecalc(vp, variants);
    setTimeout(() => scheduleRecalc(vp, variants), 0);
    setTimeout(() => scheduleRecalc(vp, variants), 120);

    const activate = (e) => {
      const t = e?.target;
      if (!t) return;
      if (!(t.matches('input[type="radio"]') || t.matches('select'))) return;

      if (!hasInteracted.get(vp)) {
        hasInteracted.set(vp, true);
      }
      scheduleRecalc(vp, variants);

      // extra passes to catch Horizon’s late updates
      setTimeout(() => scheduleRecalc(vp, variants), 0);
      setTimeout(() => scheduleRecalc(vp, variants), 50);
      setTimeout(() => scheduleRecalc(vp, variants), 120);
    };

    vp.addEventListener("click", activate);
    vp.addEventListener("input", activate);
    vp.addEventListener("change", activate);

    attachPickerObserver(vp, variants);
  }

  function scanAndWire() {
    qsa(document, "variant-picker[data-product-url]").forEach(vp => {
      wireVariantPicker(vp).catch(() => {});
    });
  }

  function startGlobalWatcher() {
    scanAndWire();

    const globalObs = new MutationObserver(() => {
      // If pickers get replaced, re-wire them
      scanAndWire();
    });

    globalObs.observe(document.documentElement, { subtree: true, childList: true });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", startGlobalWatcher);
  } else {
    startGlobalWatcher();
  }
})();
